/**
 * Readable text for a widget's scope (server/scope.js): the dates, filters,
 * population and sources behind its numbers. The card's Scope panel and the
 * CSV metadata header both use these, so the screen and the file say the same.
 */

import { fmtDate } from "./format.js";

export const FILTER_LABELS = {
  platform: "Platform",
  // The reader's profile speciality, not the content's (plan A9).
  specialty: "Audience speciality",
  profession: "Profession",
  city: "City",
  state: "State",
  country: "Country",
  source: "utm_source",
  medium: "utm_medium",
  campaign: "utm_campaign",
  contentType: "Format",
  contentSpecialty: "Content speciality",
};

const range = (from, to) => `${fmtDate(from)} – ${fmtDate(to)}`;

/** Lines describing which dates the card read. `page` = response context { window }. */
export function windowLines(scope, page) {
  const w = scope?.window;
  const pw = page?.window;
  if (!w || !pw) return [];
  const out = [];
  if (w.period) out.push(`${range(pw.from, pw.to)} (selected period, IST days)`);
  if (w.lookbackDays) out.push(`Also reads ${w.lookbackDays} days before the period`);
  if (w.previous) out.push(`Compared with ${range(pw.prevFrom, pw.prevTo)}`);
  for (const since of w.since ?? []) out.push(`${range(since, pw.to)} (fixed span, not the selected period)`);
  if (w.toDate && !w.period) out.push(`Everything up to ${fmtDate(pw.to)}`);
  if (w.allDates) out.push("Part of this card is not limited by the date range");
  return out;
}

/** "Speciality = Cardiology" style labels for a list of filter keys. */
export function filterLabels(keys, values = {}) {
  return keys.map((k) => (values[k] ? `${FILTER_LABELS[k] ?? k} = ${values[k]}` : FILTER_LABELS[k] ?? k));
}

export function testUsersLine(scope) {
  switch (scope?.testUsers) {
    case "excluded":
      return "Test and internal users excluded";
    case "ids-only":
      return "Listed test accounts excluded; other internal account types are not";
    case "included":
      return "Test and internal users included";
    default:
      return "";
  }
}

export function sourceLine(s) {
  if (s.kind === "rollup") return `${s.label}${s.refreshedAt ? ` · updated ${s.refreshedAt} IST` : ""}`;
  if (s.kind === "synced") return `${s.label} · copy of the app database`;
  return `${s.label} · live`;
}

/** True when a filter the user set did not reach this card's numbers. */
export const hasUnapplied = (scope) => Boolean(scope?.filters?.notApplied?.length);

/**
 * Metadata rows for the top of a CSV: [field, value]. `page` is the report
 * response ({ tenant, window, filters, includeTest, generatedAt }); `widget`
 * is the card as shown (with the chosen variant).
 */
export function csvMetadata(widget, page) {
  const scope = widget.scope;
  const rows = [
    ["Report", widget.title || "Key metrics"],
    ["Tenant", page?.tenant?.name ?? ""],
  ];
  if (page?.content) rows.push(["Item", `${page.content.typeLabel} ${page.content.id}${page.content.title ? ` · ${page.content.title}` : ""}`]);
  if (page?.user) rows.push(["User", page.user.uid]);
  const dates = windowLines(scope, page);
  rows.push(["Dates", dates.length ? dates.join("; ") : page?.window ? range(page.window.from, page.window.to) : ""]);
  const set = page?.filters ?? {};
  rows.push(["Filters set", Object.keys(set).length ? filterLabels(Object.keys(set), set).join("; ") : "None"]);
  if (scope) {
    rows.push(["Filters applied to this card", scope.filters.applied.length ? filterLabels(scope.filters.applied, set).join("; ") : "None"]);
    if (scope.filters.notApplied.length) rows.push(["NOT applied to this card", filterLabels(scope.filters.notApplied, set).join("; ")]);
    const pop = testUsersLine(scope);
    if (pop) rows.push(["Population", pop]);
    rows.push(["Sources", scope.sources.map(sourceLine).join("; ")]);
    for (const n of scope.notes) rows.push(["Coverage", n.text]);
  }
  if (widget.measure) rows.push(["Measurement", `${MEASURE_LABELS[widget.measure] ?? widget.measure}${widget.measureNote ? `: ${widget.measureNote}` : ""}`]);
  if (widget.variantLabel) rows.push(["View", widget.variantLabel]);
  if (widget.info) rows.push(["Definition", widget.info]);
  if (page?.dataAsOf) rows.push(["Data as of", `${page.dataAsOf} IST`]);
  rows.push(["Generated", page?.generatedAt ?? new Date().toISOString()]);
  return rows;
}

/**
 * Whether a chart point is still in progress: today on a daily axis, the
 * current month on a monthly axis (IST). Charts draw it dashed or faded and
 * label it "partial"; tables and CSVs add "(partial)" to its period.
 */
export function isPartialPoint(x, xFormat, today) {
  const s = String(x ?? "");
  if (xFormat === "day") return s.slice(0, 10) === today;
  if (xFormat === "month") return s.slice(0, 7) === today.slice(0, 7);
  return false;
}

/**
 * "Data as of 14:30 IST" for a page header, from the oldest refresh among the
 * summaries the page read (`dataAsOf`, IST "yyyy-mm-dd hh:mm"). Adds that
 * today is still in progress when the window ends today. Empty when the page
 * read no summary table (live sources only).
 */
export function freshnessLine(page, today) {
  const at = page?.dataAsOf;
  const partialToday = page?.window?.to === today;
  if (!at) return partialToday ? "Today is still in progress" : "";
  const [day, time] = at.split(" ");
  const when = day === today ? `${time} IST` : `${fmtDate(day)}, ${time} IST`;
  return `Data as of ${when}${partialToday ? " · today is still in progress" : ""}`;
}

/**
 * The four measurement states (server MEASURE in reports/kit.js). "Measured" is
 * the default and gets no badge; the other three are always labelled, so a
 * proxy or a missing signal is never read as a plain measurement or as zero.
 */
export const MEASURE_LABELS = {
  measured: "Measured",
  proxy: "Proxy",
  not_measured: "Not measured",
  not_applicable: "Not applicable",
};
export const MEASURE_TONES = { measured: "good", proxy: "warning", not_measured: "neutral", not_applicable: "neutral" };
export const MEASURE_HINTS = {
  measured: "An observed event in a known scope.",
  proxy: "An indirect observation; read it with its limitation.",
  not_measured: "The signal is absent or unverified. This is not zero.",
  not_applicable: "Does not fit this selection or the content's goal.",
};
